import type { EmployeeRowDto } from '../api/hr-employee';
import type { QuotaRowDto } from '../api/hr-admin';

/**
 * 인사 홈이 보여 줄 숫자 — **집계는 여기서만** 한다.
 *
 * ── 왜 새 API 가 없나 ────────────────────────────────────────────────────
 * 시안(A1)의 대시보드는 «재직 인원 · 이번 달 입사 · 근태 이상 · 연차 소진율» 을 요구하는데,
 * 그것을 한 번에 주는 엔드포인트는 없다. 다만 **인사 담당자가 이미 부르는 두 목록**
 * — 직원 명부(`listEmployees`)와 연차 현황(`listLeaveQuotas`) — 안에 그 값이 다 들어 있다.
 * 그래서 표를 만들지도, 라우트를 늘리지도 않고 **있는 두 응답을 세는 것**으로 채운다.
 *
 * 오늘 근태(출근·휴가·미출근)만 여기 없다 — 팀 근태 API 는 **한 번에 한 팀**이라
 * 전사를 그리려면 팀 수만큼 불러야 한다. 홈에서 그 값을 쓰지 않고 «근태 현황» 으로 보낸다.
 *
 * ── 날짜를 밖에서 받는 이유 ──────────────────────────────────────────────
 * `new Date()` 를 안에서 부르면 테스트가 «오늘» 에 따라 흔들린다(고정 날짜 취약성).
 * 부르는 쪽이 `YYYY-MM` 을 넘기고, 비교는 **문자열 앞자리**로만 한다 — 시간대 연산이 없다
 * (서버 시간대는 KST 이고 hireDate 는 날짜만 있는 값이다).
 */
export interface HrHomeTeamUsage {
  team: string;
  total: number;
  used: number;
  /** 0~100. 부여가 0이면 0. */
  rate: number;
}

export interface HrHomeStats {
  /** 재직(ACTIVE) — 휴직·퇴사는 빠진다. */
  active: number;
  contract: number;
  onLeave: number;
  resigned: number;
  /** 이번 달 입사한 사람(입사일 오름차순). */
  hiredThisMonth: EmployeeRowDto[];
  /** 입사일이 없다 = 연차 계산이 시작되지 않았다. 재직자만 센다. */
  missingHireDate: number;
  /** 소속 팀이 없다 = 결재 사다리의 팀장 칸이 비어 있다. */
  missingTeam: number;
  quota: {
    total: number;
    used: number;
    remain: number;
    /** 소진율 0~100(소수 첫째 자리). */
    rate: number;
    /** 연차가 아직 산정되지 않은 사람 수(annualTotal 이 null). */
    missing: number;
  };
  /** 팀별 소진율 — 낮은 순(덜 쓴 팀이 먼저 온다. 촉진 대상이라 그렇다). */
  teamUsage: HrHomeTeamUsage[];
}

const round1 = (n: number) => Math.round(n * 10) / 10;

export function buildHrHome(
  employees: readonly EmployeeRowDto[],
  quotas: readonly QuotaRowDto[],
  yearMonth: string,
): HrHomeStats {
  const activeRows = employees.filter((e) => e.employmentStatus === 'ACTIVE');
  const hiredThisMonth = employees
    .filter((e) => (e.hireDate ?? '').startsWith(yearMonth))
    .sort((a, b) => (a.hireDate ?? '').localeCompare(b.hireDate ?? ''));

  let total = 0;
  let used = 0;
  let missing = 0;
  const byTeam = new Map<string, { total: number; used: number }>();
  for (const q of quotas) {
    if (q.annualTotal === null) missing += 1;
    total += q.annualTotal ?? 0;
    used += q.usedDays;
    // ★ 팀이 없는 사람의 묶음 이름 — 조직도와 **같은 말**을 쓴다(2026-08-31 · 「회사 직속」).
    const key = q.teamName ?? '회사 직속';
    const cur = byTeam.get(key) ?? { total: 0, used: 0 };
    cur.total += q.annualTotal ?? 0;
    cur.used += q.usedDays;
    byTeam.set(key, cur);
  }

  const teamUsage = [...byTeam.entries()]
    .map(([team, v]) => ({
      team,
      total: round1(v.total),
      used: round1(v.used),
      rate: v.total > 0 ? round1((v.used / v.total) * 100) : 0,
    }))
    .sort((a, b) => a.rate - b.rate);

  return {
    active: activeRows.length,
    contract: activeRows.filter((e) => e.employmentType === 'CONTRACT').length,
    onLeave: employees.filter((e) => e.employmentStatus === 'LEAVE').length,
    resigned: employees.filter((e) => e.employmentStatus === 'RESIGNED').length,
    hiredThisMonth,
    missingHireDate: activeRows.filter((e) => !e.hireDate).length,
    missingTeam: activeRows.filter((e) => !e.teamId).length,
    quota: {
      total: round1(total),
      used: round1(used),
      remain: round1(total - used),
      rate: total > 0 ? round1((used / total) * 100) : 0,
      missing,
    },
    teamUsage,
  };
}

/** `YYYY-MM` — 부르는 쪽이 «오늘» 을 정한다(테스트는 고정 값을 넘긴다). */
export function yearMonthOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

/* ────────────────────────────────────────────────────────────────────
 * 인사 홈 — 시안 A1 이 요구하는 세 덩어리 (2026-08-27)
 *   · 월별 입 · 퇴사   · 최근 인사 이벤트   · 이번 달 퇴사
 * ★ 전부 **이미 부르는 두 응답**(직원 명부 · 발령 목록)에서 센다 — 새 API 는
 *   «오늘 근태» 하나뿐이고, 그건 팀 근태가 한 팀씩이라 어쩔 수 없었다.
 * ──────────────────────────────────────────────────────────────────── */

/** 한 달치 들고남. */
export interface HrFlowMonth {
  /** 1~12. */
  month: number;
  hired: number;
  left: number;
}

/** `YYYY-MM-DD` 에서 연·월을 읽는다. 형식이 아니면 null. */
const ymOf = (s: string | null | undefined): [number, number] | null => {
  if (!s || s.length < 7) return null;
  const y = Number(s.slice(0, 4));
  const m = Number(s.slice(5, 7));
  return Number.isInteger(y) && m >= 1 && m <= 12 ? [y, m] : null;
};

/**
 * 월별 입 · 퇴사 — 열두 칸을 **늘 채워** 돌려준다(빈 달도 0 으로 선다).
 *
 * ★ 입사는 `hireDate`, 퇴사는 **적용된 퇴직 발령**에서 센다. 상태만 «퇴사» 로 바뀐 사람은
 *   날짜가 없어 어느 달에 세울지 알 수 없다 — 그림에 넣지 않는다(인사 통계와 같은 규칙).
 */
export function monthlyFlow(
  employees: ReadonlyArray<{ hireDate: string | null }>,
  resigns: ReadonlyArray<{ date: string }>,
  year: number,
): HrFlowMonth[] {
  const rows: HrFlowMonth[] = Array.from({ length: 12 }, (_, i) => ({ month: i + 1, hired: 0, left: 0 }));
  for (const e of employees) {
    const ym = ymOf(e.hireDate);
    if (ym && ym[0] === year) rows[ym[1] - 1]!.hired += 1;
  }
  for (const r of resigns) {
    const ym = ymOf(r.date);
    if (ym && ym[0] === year) rows[ym[1] - 1]!.left += 1;
  }
  return rows;
}

/** 그 달에 나간 사람 수. `yearMonth` 는 `YYYY-MM`. */
export function leftInMonth(
  resigns: ReadonlyArray<{ date: string }>,
  yearMonth: string,
): number {
  return resigns.filter((r) => r.date.slice(0, 7) === yearMonth).length;
}

/** 최근 인사 이벤트 한 줄. */
export interface HrEventRow {
  key: string;
  /** `YYYY-MM-DD`. */
  date: string;
  name: string;
  /** «입사» · «승진» 처럼 사람이 읽는 말. */
  kind: string;
  /** 무엇이 무엇으로 바뀌었나 — 없으면 빈 문자열. */
  detail: string;
}

/**
 * 최근 인사 이벤트 — **적용된 발령**과 **입사**를 한 줄기로 세워 최신순으로 준다.
 *
 * ★ 예정(`PENDING`)은 넣지 않는다 — 아직 일어나지 않은 일이라 «이벤트» 가 아니다.
 *   그것은 «처리 대기» 가 맡는다.
 * ★ 입사는 발령에 안 남는 회사가 많아(계정을 만들면 그만) `hireDate` 에서 따로 세운다.
 *   같은 사람이 발령에도 있으면 **발령이 이긴다** — 그쪽이 더 자세하다.
 */
export function recentEvents(
  assignments: ReadonlyArray<{
    id: string; userId: string; userName: string | null; kindLabel: string;
    status: string; effectiveDate: string;
    before?: { teamName?: string | null; position?: string | null };
    after?: { teamName?: string | null; position?: string | null };
  }>,
  hired: ReadonlyArray<{ userId: string; name: string; hireDate: string | null; teamName: string | null }>,
  limit = 6,
): HrEventRow[] {
  const rows: HrEventRow[] = [];
  const seen = new Set<string>();

  for (const a of assignments) {
    if (a.status !== 'APPLIED') continue;
    const to = [a.after?.teamName, a.after?.position].filter(Boolean).join(' · ');
    const from = [a.before?.teamName, a.before?.position].filter(Boolean).join(' · ');
    rows.push({
      key: `a:${a.id}`,
      date: a.effectiveDate,
      name: a.userName ?? '—',
      kind: a.kindLabel,
      detail: to && from && to !== from ? `${from} → ${to}` : to || from,
    });
    seen.add(`${a.userId}:${a.effectiveDate}`);
  }

  for (const e of hired) {
    if (!e.hireDate) continue;
    if (seen.has(`${e.userId}:${e.hireDate}`)) continue;
    rows.push({
      key: `h:${e.userId}`,
      date: e.hireDate,
      name: e.name,
      kind: '입사',
      detail: e.teamName ?? '회사 직속',
    });
  }

  return rows
    .sort((x, y) => (x.date < y.date ? 1 : x.date > y.date ? -1 : x.name.localeCompare(y.name, 'ko')))
    .slice(0, limit);
}
